import { useState } from 'react';
import { motion } from 'framer-motion';
import useGameStore from '@/store/gameStore';
import websocketService from '@/services/websocket';
import toast from 'react-hot-toast';

function GuessingPhase() {
  const { gameState, isGuesser } = useGameStore();
  const [guess, setGuess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!gameState?.round) return null;

  const isCurrentGuesser = isGuesser();
  const visibleClues = Object.entries(gameState.round.clues).filter(
    ([_, clue]) => clue.status === 'VISIBLE'
  );

  const handleSubmitGuess = async () => {
    const trimmedGuess = guess.trim();

    if (!trimmedGuess) {
      toast.error('Please enter your guess');
      return;
    }

    setIsSubmitting(true);
    websocketService.submitGuess(trimmedGuess);
    setIsSubmitting(false);
  };

  if (isCurrentGuesser) {
    return (
      <div className="py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg mx-auto"
        >
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            Your Turn to Guess! 🎯
          </h2>

          {visibleClues.length === 0 ? (
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">😅</div>
              <p className="text-lg text-gray-600 mb-2">
                Oops! All clues were duplicates or invalid
              </p>
              <p className="text-sm text-gray-500">You&apos;ll have to guess without any clues!</p>
            </div>
          ) : (
            <>
              <p className="text-center text-gray-600 mb-4">
                Based on these clues, what&apos;s the mystery word?
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {visibleClues.map(([userId, clue], index) => (
                  <motion.div
                    key={userId}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="clue-card-visible text-center"
                  >
                    <p className="text-lg font-bold text-primary-600">{clue.text}</p>
                  </motion.div>
                ))}
              </div>
            </>
          )}

          <div className="space-y-4">
            <input
              type="text"
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              placeholder="Enter your guess..."
              disabled={isSubmitting || gameState.round.guess !== null}
              data-testid="input-guess"
              className="input text-center text-lg font-semibold"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !gameState.round?.guess) {
                  handleSubmitGuess();
                }
              }}
              autoFocus
            />

            {!gameState.round.guess ? (
              <div className="flex gap-3">
                <button
                  onClick={handleSubmitGuess}
                  disabled={!guess.trim() || isSubmitting}
                  data-testid="btn-submit-guess"
                  className="flex-1 btn-primary"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Guess'}
                </button>
                <button
                  onClick={() => websocketService.skipCard()}
                  disabled={isSubmitting}
                  className="btn-danger"
                  data-testid="btn-skip"
                >
                  Skip
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800">
                  Guess submitted: <span className="text-primary-600">{gameState.round.guess}</span>
                </p>
                <p className="text-sm text-gray-500 mt-2">Waiting for results...</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // View for non-guessers
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
      >
        <div className="text-6xl mb-4">👀</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {gameState.players[gameState.round.activeGuesserId]?.name} is Guessing
        </h2>

        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-2">The word was:</p>
          <p className="text-3xl font-bold text-primary-600">{gameState.round.targetWord}</p>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-3">Valid clues shown:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {visibleClues.map(([userId, clue]) => (
              <span
                key={userId}
                className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold"
              >
                {clue.text}
              </span>
            ))}
            {visibleClues.length === 0 && (
              <span className="text-gray-500 italic">No valid clues!</span>
            )}
          </div>
        </div>

        {gameState.round.guess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-blue-50 rounded-lg"
          >
            <p className="text-sm text-blue-600">Their guess:</p>
            <p className="text-xl font-bold text-blue-700">{gameState.round.guess}</p>
          </motion.div>
        )}

        {!gameState.round.guess && (
          <div className="flex justify-center gap-1">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 bg-primary-400 rounded-full"
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default GuessingPhase;
